import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, GraduationCap, TrendingUp } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";

/**
 * Informed OS Academy — my learning dashboard (Phase 1b).
 *
 * Route: `/academy/my-learning`.
 *
 * Fetches `GET /api/academy/enrollments/me` and `GET /api/academy/dashboard/me`
 * in parallel. Progress bars render at 0% in 1b (real math ships with
 * the Phase 1c progress engine).
 */
export default function MyLearning() {
  const [state, setState] = useState({
    status: "loading",
    enrollments: [],
    dashboard: null,
    error: null,
  });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const [enrRes, dashRes] = await Promise.all([
        api.get("/academy/enrollments/me"),
        api.get("/academy/dashboard/me"),
      ]);
      setState({
        status: "ready",
        enrollments: enrRes.data?.items || [],
        dashboard: dashRes.data || null,
        error: null,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (err?.response?.status === 404 && code === "feature_disabled") {
        setState({ status: "disabled", enrollments: [], dashboard: null, error: null });
        return;
      }
      setState({ status: "error", enrollments: [], dashboard: null, error: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="academy-my-learning"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-24">
        <header className="mb-10">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border"
            style={{
              background: "var(--v2-accent-soft)",
              borderColor: "var(--v2-accent)",
              color: "var(--v2-accent)",
            }}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            My Learning
          </div>
          <h1
            data-testid="academy-my-learning-title"
            className="mt-6 text-[32px] md:text-[42px] font-semibold tracking-tight text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Your enrolled programs.
          </h1>
        </header>

        {state.status === "loading" ? (
          <div data-testid="academy-my-learning-loading" className="text-v2-text-muted">Loading…</div>
        ) : null}

        {state.status === "disabled" ? (
          <div
            data-testid="academy-my-learning-disabled"
            className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
          >
            <p className="text-v2-text font-semibold">Academy is not yet available.</p>
            <p className="mt-3 text-sm text-v2-text-muted">Please check back later.</p>
          </div>
        ) : null}

        {state.status === "error" ? (
          <div
            data-testid="academy-my-learning-error"
            className="rounded-xl border border-[var(--v2-danger)] bg-v2-surface p-6 text-v2-text"
          >
            <p className="font-semibold text-[var(--v2-danger)]">Couldn't load your learning dashboard.</p>
            <p className="mt-2 text-sm text-v2-text-muted">{state.error}</p>
            <button
              data-testid="academy-my-learning-retry"
              type="button"
              onClick={load}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-v2-border text-sm hover:bg-v2-surface-muted"
            >
              Retry
            </button>
          </div>
        ) : null}

        {state.status === "ready" ? (
          <>
            {state.dashboard ? (
              <>
                <section
                  data-testid="academy-my-learning-summary"
                  className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
                >
                  <SummaryTile testId="tile-enrolled" label="Enrolled" value={state.dashboard.enrolled_count} icon={BookOpen} />
                  <SummaryTile testId="tile-active" label="Active" value={state.dashboard.active_count} icon={TrendingUp} />
                  <SummaryTile testId="tile-in-progress" label="In progress" value={state.dashboard.in_progress_count} icon={ArrowRight} />
                  <SummaryTile testId="tile-completed" label="Completed" value={state.dashboard.completed_count} icon={CheckCircle2} />
                </section>
                {state.dashboard.most_recent_program?.current_lesson ? (
                  <ResumeCard mostRecent={state.dashboard.most_recent_program} />
                ) : null}
              </>
            ) : null}

            {state.enrollments.length === 0 ? (
              <div
                data-testid="academy-my-learning-empty"
                className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
              >
                <BookOpen className="w-8 h-8 mx-auto text-v2-text-muted" />
                <p className="mt-4 text-v2-text font-semibold">
                  You haven't enrolled in any programs yet.
                </p>
                <Link
                  to="/academy"
                  data-testid="academy-my-learning-browse-cta"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--v2-accent)] text-white text-sm font-semibold hover:opacity-90"
                >
                  Browse Academy
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <ul
                data-testid="academy-my-learning-enrollments"
                className="space-y-4"
              >
                {state.enrollments.map((enr) => (
                  <EnrollmentCard key={enr.id} enr={enr} />
                ))}
              </ul>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}

function SummaryTile({ testId, label, value, icon: Icon }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-v2-border bg-v2-surface p-4"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-v2-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-v2-text-muted" />
      </div>
      <p className="mt-3 text-[28px] font-semibold text-v2-text tabular-nums">{value ?? 0}</p>
    </div>
  );
}

function ResumeCard({ mostRecent }) {
  const l = mostRecent?.current_lesson;
  if (!l) return null;
  const pct = Math.max(0, Math.min(100, Number(mostRecent.progress_pct) || 0));
  return (
    <section
      data-testid="academy-my-learning-resume"
      className="mb-10 rounded-xl border border-[var(--v2-accent)] bg-v2-surface p-6"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--v2-accent)]">
            Resume learning
          </p>
          <p
            className="mt-1 text-[20px] font-semibold text-v2-text truncate"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {mostRecent.title}
          </p>
          <p className="mt-1 text-sm text-v2-text-muted truncate">
            Next up: <span className="text-v2-text">{l.title}</span>
          </p>
          <div className="mt-3 h-1.5 w-full max-w-md bg-v2-surface-muted rounded-full overflow-hidden">
            <div
              data-testid="academy-my-learning-resume-progress"
              className="h-full bg-[var(--v2-accent)]"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-v2-text-muted">{pct}% complete</p>
        </div>
        <Link
          to={`/academy/programs/${mostRecent.program_slug}/lessons/${l.slug}`}
          data-testid="academy-my-learning-resume-cta"
          className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[var(--v2-accent)] text-white text-sm font-semibold hover:opacity-90"
        >
          Continue lesson
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function EnrollmentCard({ enr }) {
  const p = enr.program || {};
  const progress = Math.max(0, Math.min(100, Number(enr.progress_pct) || 0));
  const statusLabel = ({
    active: "Active",
    completed: "Completed",
    withdrawn: "Withdrawn",
    paused: "Paused",
  })[enr.status] || enr.status;
  return (
    <li
      data-testid={`academy-my-learning-card-${p.slug || enr.id}`}
      className="rounded-xl border border-v2-border bg-v2-surface p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3
            className="text-[18px] font-semibold text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {p.title || "Untitled program"}
          </h3>
          {p.subtitle ? (
            <p className="mt-1 text-sm text-v2-text-muted line-clamp-2">{p.subtitle}</p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-v2-text-muted">
            <span>{p.module_count ?? 0} module{p.module_count === 1 ? "" : "s"}</span>
            <span>·</span>
            <span>{p.lesson_count ?? 0} lesson{p.lesson_count === 1 ? "" : "s"}</span>
            <span>·</span>
            <span
              data-testid={`academy-enrollment-status-${enr.id}`}
              className={enr.status === "active" ? "text-[var(--v2-tier-ready)]" : "text-v2-text-muted"}
            >
              {statusLabel}
            </span>
          </div>
        </div>
        {p.slug ? (
          <Link
            to={`/academy/programs/${p.slug}`}
            data-testid={`academy-my-learning-view-${p.slug}`}
            className="shrink-0 inline-flex items-center gap-2 text-sm text-v2-accent hover:underline"
          >
            View
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : null}
      </div>
      <div className="mt-5">
        <div className="h-1.5 w-full bg-v2-surface-muted rounded-full overflow-hidden">
          <div
            data-testid={`academy-enrollment-progress-${enr.id}`}
            className="h-full bg-[var(--v2-accent)]"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-v2-text-muted">
          <span>{progress}% complete</span>
          <span>Lesson tracking launches soon</span>
        </div>
      </div>
    </li>
  );
}
